import {
  ConciliationCaseEvent,
  ConciliationChange,
  ConciliationDerivedItem,
  ConciliationGroupItem,
} from "@/@types/conciliation-history";
import { ConciliationChangeFieldEnum } from "@/constants/conciliation-history";

import { JustificationNames } from "../../types";
import { formatAmount } from "../../utils/amount-display";
import { changeTransitionOf, itemIdLabelOf } from "../../utils/change-label";
import {
  CASE_TIMELINE_LABELS,
  CHANGE_FIELD_LABELS,
  NOT_AVAILABLE,
  TEXT_SEPARATORS,
} from "../../utils/messages";
import {
  CASE_CONCILIATION_TEST_ID,
  CASE_CONCILIATION_DERIVED_TEST_ID,
} from "../../utils/case-history-test-ids";

interface CaseConciliationBlockProps {
  event: ConciliationCaseEvent;
  justificationNames: JustificationNames;
  onOpenCase: (itemId: number) => void;
}

const groupItemsOf = (
  event: ConciliationCaseEvent
): readonly ConciliationGroupItem[] =>
  event.context.group_items ?? event.context.origin_items ?? [];

const justificationChangeOf = (
  event: ConciliationCaseEvent
): ConciliationChange | null =>
  event.changes.find(
    (change: ConciliationChange): boolean =>
      change.field === ConciliationChangeFieldEnum.JUSTIFICATION_ID
  ) ?? null;

const registerLabelOf = (item: ConciliationGroupItem): string =>
  [
    CHANGE_FIELD_LABELS[ConciliationChangeFieldEnum.REGISTER_CODE],
    item.register_code ?? NOT_AVAILABLE,
  ].join(TEXT_SEPARATORS.WORD);

const itemLabelOf = (item: ConciliationGroupItem): string =>
  [
    itemIdLabelOf(item.id),
    registerLabelOf(item),
    formatAmount(item.amount),
  ].join(TEXT_SEPARATORS.INLINE);

export function CaseConciliationBlock({
  event,
  justificationNames,
  onOpenCase,
}: CaseConciliationBlockProps): JSX.Element {
  const items: readonly ConciliationGroupItem[] = groupItemsOf(event);
  const derived: ConciliationDerivedItem | null = event.context.derived ?? null;
  const balance: string | null = event.context.balance ?? null;
  const justification: ConciliationChange | null =
    justificationChangeOf(event);

  return (
    <div
      className="mt-2 rounded bg-gray-50 p-2"
      data-testid={CASE_CONCILIATION_TEST_ID}
    >
      <span className="text-xs font-semibold text-[#613F4F]">
        {CASE_TIMELINE_LABELS.CONCILIATION_TITLE}
      </span>

      {justification && (
        <p className="text-xs text-gray-700">
          {CHANGE_FIELD_LABELS[ConciliationChangeFieldEnum.JUSTIFICATION_ID]}
          {TEXT_SEPARATORS.LABEL}
          {changeTransitionOf(justification, justificationNames)}
        </p>
      )}

      {items.length > 0 && (
        <p className="text-xs text-gray-700">
          {CASE_TIMELINE_LABELS.CONCILIATION_WITH}
          {TEXT_SEPARATORS.LABEL}
          {items.map(itemLabelOf).join(TEXT_SEPARATORS.LIST)}
        </p>
      )}

      {balance && (
        <p className="text-xs text-gray-700">
          {CASE_TIMELINE_LABELS.CONCILIATION_BALANCE}
          {TEXT_SEPARATORS.LABEL}
          {formatAmount(balance)}
        </p>
      )}

      {derived && (
        <p className="text-xs text-gray-700">
          {CASE_TIMELINE_LABELS.CONCILIATION_DERIVED}
          {TEXT_SEPARATORS.LABEL}
          {itemIdLabelOf(derived.id)}
          {TEXT_SEPARATORS.INLINE}
          {formatAmount(derived.amount)}
          {TEXT_SEPARATORS.WORD}
          <button
            type="button"
            className="underline text-[#613F4F]"
            data-testid={CASE_CONCILIATION_DERIVED_TEST_ID}
            onClick={(): void => onOpenCase(derived.id)}
          >
            {CASE_TIMELINE_LABELS.CONCILIATION_OPEN_DERIVED}
          </button>
        </p>
      )}
    </div>
  );
}
